<template>
  <div
    class="!pl-4 cursor-pointer flex flex-row items-center gap-3 !justify-between dropdown_item suite_item_row"
    :class="{ selected: isSelected || isDropdownOpen }"
    @click="$emit('click', suite)"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="flex flex-row items-center gap-2 truncate">
      <ThemeImage
        class="size-4"
        :icon="imageNames.PANEL_REPOSITORY"
        alt="suite_icon"
      />

      <span class="text-sm color_666 font-normal truncate">{{
        renderEntity(suite)
      }}</span>
    </div>

    <div
      class="flex flex-row items-center gap-1 suite_item_actions"
      :class="{ show: isDropdownOpen || isHovered }"
    >
      <div
        v-for="(action, index) in actions"
        :key="`${action?.id}_action_${index}`"
        @click.stop="handleActionClick(action)"
      >
        <a-dropdown
          v-if="action?.options?.length > 0"
          trigger="click"
          placement="bottomLeft"
          :get-popup-container="(trigger) => trigger.parentElement"
          @visibleChange="handleDropdownChange($event, action.id)"
        >
          <a-tooltip placement="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">{{
                action?.title
              }}</span>
            </template>
            <div
              class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper"
              :class="{ selected: activeDropdown === action.id }"
            >
              <ThemeImage
                class="size-4"
                :icon="action?.icon"
                :alt="`${action?.id}_icon`"
              />
            </div>
          </a-tooltip>

          <template #overlay>
            <a-menu class="dropdown_menu">
              <a-menu-item
                v-for="(option, oIndex) in action?.options"
                :key="`${option?.id}_option_${oIndex}`"
                @click="handleOptionClick(option, suite)"
              >
                <div class="flex flex-row items-center gap-2">
                  <ThemeImage
                    class="size-4"
                    :icon="option?.icon"
                    :alt="`${option?.id}_icon`"
                  />
                  <span class="text-sm primary_color">{{ option?.title }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <a-tooltip
          v-else-if="action?.tooltip"
          placement="top"
          color="var(--tooltip-bg)"
        >
          <template #title>
            <span class="text-[13px] font-normal primary_color">{{
              action?.title
            }}</span>
          </template>
          <ThemeImage
            class="size-4"
            :icon="action?.icon"
            :alt="`${action?.id}_icon`"
          />
        </a-tooltip>
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Suite"
      :message="`Are you sure you want to delete ${suite?.name} suite?`"
      :loading="isDeleting"
      type="delete"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
      confirm-text="Delete"
      loading-text="Deleting..."
    />

    <AddOrUpdateSuiteSection
      v-if="showEditModal"
      source="suite_item_edit"
      :visible="showEditModal"
      @update:visible="showEditModal = $event"
      :suite="suite"
      :space="null"
      mode="edit"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { renderEntity } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { qaEvents } from "@/src/constants/qaConstants.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import AddOrUpdateSuiteSection from "@/src/components/spaces_components/qa/AddOrUpdateSuiteSection.vue";
import QAService from "@/src/api/QAService.js";
import eventBus from "@/src/utils/eventBus.js";

const props = defineProps({
  suite: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "action-click", "option-click"]);

const activeDropdown = ref(null);
const isDropdownOpen = ref(false);
const isHovered = ref(false);
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const isDeleting = ref(false);

const actions = [
  {
    id: "more_options",
    title: "More options",
    icon: imageNames.MORE_ICON_GREY,
    size: 14,
    tooltip: true,
    options: [
      {
        id: "edit",
        title: "Edit",
        icon: imageNames.EDIT,
        size: 14,
        action: () => {
          activeDropdown.value = null;
          isDropdownOpen.value = false;
          isHovered.value = false;
          showEditModal.value = true;
        },
      },
      {
        id: "delete",
        title: "Delete",
        icon: imageNames.DELETE_GREY,
        size: 14,
        action: () => {
          activeDropdown.value = null;
          isDropdownOpen.value = false;
          isHovered.value = false;
          showDeleteModal.value = true;
        },
      },
    ],
  },
];

const handleActionClick = (action) => {
  try {
    if (!action?.options?.length && action?.action) {
      action.action();
    }
    emit("action-click", action);
  } catch (error) {
    console.error("An error occurred while handling action click: ", error);
  }
};

const handleOptionClick = (option, suite) => {
  try {
    if (option?.action) {
      option.action(suite);
    }
    emit("option-click", option, suite);
  } catch (error) {
    console.error("An error occurred while handling option click: ", error);
  }
};

const handleMouseEnter = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

const handleDropdownChange = (visible, actionId) => {
  try {
    if (visible) {
      activeDropdown.value = actionId;
      isDropdownOpen.value = true;
    } else {
      if (activeDropdown.value === actionId) {
        activeDropdown.value = null;
        isDropdownOpen.value = false;
      }
    }
  } catch (error) {
    console.error("An error occurred while handling dropdown change: ", error);
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;

    await QAService.deleteSuite(props.suite?.id);
    message.success(`Suite ${props.suite?.name} deleted successfully!`);
    showDeleteModal.value = false;
    eventBus.emit(qaEvents.SUITE_MANIPULATED, qaEvents.DELETED);
  } catch (error) {
    console.error("An error occurred while deleting a suite: ", error);
    message.error("An error occurred while deleting suite!");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.suite_item_actions {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.suite_item_actions.show {
  opacity: 1;
  visibility: visible;
}
</style>
